import { useEffect, useMemo, useState } from 'react'
import { isCancel } from 'axios'
import { listHistoryCustomers } from '@/api/history'
import { getApiErrorMessage } from '@/lib/apiError'
import {
  countByState,
  describeRound,
  HISTORY_STATE_LABELS,
  HISTORY_STATE_VALUES,
  matchesHistorySearch,
  RECORD_STATE_KEYS,
  RECORD_STATE_LABELS,
  recordStateKeyOf,
  roundsOf
} from '@/lib/history'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { HistoryCustomerRow } from '@/components/history/HistoryCustomerRow'
import { HistoryWorklistUpload } from '@/components/history/HistoryWorklistUpload'
import type { RecordStateKey } from '@/lib/history'
import type { HistoryCustomerSummary, HistoryState } from '@/api/types'

const LOAD_ERROR_MESSAGE = 'Arbetslistan kunde inte hämtas.'
const ALL = 'all'

type StateFilter = HistoryState | typeof ALL
type RecordFilter = RecordStateKey | typeof ALL

export function HistoryPage(): React.ReactElement {
  const [rows, setRows] = useState<HistoryCustomerSummary[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState<StateFilter>('pending')
  const [recordFilter, setRecordFilter] = useState<RecordFilter>(ALL)
  const [roundFilter, setRoundFilter] = useState<string>(ALL)

  const loadRows = async (signal?: AbortSignal): Promise<void> => {
    setIsLoading(true)
    setErrorMessage(null)

    try {
      setRows(await listHistoryCustomers(signal))
    } catch (error) {
      if (isCancel(error)) return
      setErrorMessage(getApiErrorMessage(error, LOAD_ERROR_MESSAGE))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const controller = new AbortController()

    void loadRows(controller.signal)

    return () => controller.abort()
  }, [])

  const counts = useMemo(() => countByState(rows), [rows])
  const rounds = useMemo(() => roundsOf(rows), [rows])
  const visible = useMemo(
    () =>
      rows.filter(
        (row) =>
          (stateFilter === ALL || row.state === stateFilter) &&
          (recordFilter === ALL || recordStateKeyOf(row.record_status, row.record_next_sequence) === recordFilter) &&
          (roundFilter === ALL || String(row.round) === roundFilter) &&
          matchesHistorySearch(row, search)
      ),
    [rows, stateFilter, recordFilter, roundFilter, search]
  )

  const hasRows = rows.length > 0
  const isEmpty = !isLoading && !hasRows
  const hasRounds = rounds.length > 1

  return (
    <div className="space-y-6">
      <Card className="shadow-none border-border/80">
        <CardHeader>
          <CardTitle className="text-3xl font-serif text-primary">Historik</CardTitle>
          <CardDescription>
            Alla kunder, en i taget. Öppna kunden, jämför plattformen med avstämningens förslag, ändra det som behövs, förhandsgranska och
            lägg in. Stämmer allt redan: markera som kontrollerad.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 text-sm sm:grid-cols-5">
            <HistoryCount
              label="Kvar"
              value={counts.pending}
            />
            <HistoryCount
              label="Inlagda"
              value={counts.applied}
            />
            <HistoryCount
              label="Kontrollerade"
              value={counts.checked}
            />
            <HistoryCount
              label="Hoppade över"
              value={counts.skipped}
            />
            <HistoryCount
              label="Flaggade"
              value={counts.flagged}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-none border-border/80">
        <CardHeader>
          <CardTitle>Kunder</CardTitle>
          <CardDescription>
            Sök på namn, personnummer eller kundnummer. Filtrera på arbetsläge, på vad avstämningen säger om kunden och på omgång.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_12rem_16rem_12rem]">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Sök"
            />
            <Select
              value={stateFilter}
              onValueChange={(value) => setStateFilter(value as StateFilter)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Alla arbetslägen</SelectItem>
                {HISTORY_STATE_VALUES.map((state) => (
                  <SelectItem
                    key={state}
                    value={state}
                  >
                    {HISTORY_STATE_LABELS[state]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={recordFilter}
              onValueChange={(value) => setRecordFilter(value as RecordFilter)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Alla lägen enligt avstämningen</SelectItem>
                {RECORD_STATE_KEYS.map((key) => (
                  <SelectItem
                    key={key}
                    value={key}
                  >
                    {RECORD_STATE_LABELS[key]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasRounds && (
              <Select
                value={roundFilter}
                onValueChange={setRoundFilter}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Alla omgångar</SelectItem>
                  {rounds.map((round) => (
                    <SelectItem
                      key={round}
                      value={String(round)}
                    >
                      {describeRound(round)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
          {isEmpty && <p className="text-sm text-muted-foreground">Ingen arbetslista inläst än.</p>}
          {hasRows && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/50 text-left text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Kund nr</th>
                    <th className="px-3 py-2 font-medium">Namn</th>
                    <th className="px-3 py-2 font-medium">Läge enligt avstämningen</th>
                    <th className="px-3 py-2 font-medium">Föreslås läggas in</th>
                    <th className="px-3 py-2 font-medium">Arbetsläge</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <HistoryCustomerRow
                      key={row.customer_id}
                      row={row}
                    />
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-xs text-muted-foreground">
                {visible.length} av {rows.length} kunder
              </p>
            </div>
          )}
          {isLoading && <p className="text-sm text-muted-foreground">Hämtar…</p>}
        </CardContent>
      </Card>

      <HistoryWorklistUpload onUploaded={() => loadRows()} />
    </div>
  )
}

function HistoryCount({ label, value }: { label: string; value: number }): React.ReactElement {
  return (
    <div className="rounded-md border border-border/80 px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold tabular-nums">{value}</p>
    </div>
  )
}
